import {Avatar, Button, Input, message, Modal} from 'antd'
import {
    course_assignTeacherToCourse_Api,
    course_editTeacherFromCourse_Api,
    course_getCourseTeacherFilterOptions_Api,
    course_getCourseTeachers_Api,
    course_revokeTeacherFromCourse_Api,
    organization_getGroups_Api,
} from 'apis'
import {getId, getType} from 'apis/model/base'
import {createSelectValue} from 'components/form/utils'
import LookupField from 'components/LookupField'
import EmptyHolder, {renderEmptyHolder} from 'components/EmptyHolder'
import {ItemInformation} from 'components/common/ItemInformation'
import getTitle from 'helpers/getTitle'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useMemo, useState} from 'react'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {getResponseItem, getResponseItems, NullAsDefault, renderIf} from 'views/Shared'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {TeacherSelector} from '../../components/TeacherSelector'
import CourseContext from '../../CourseContext';
import {Field, Section} from "../../../../../components/form/ModernForm";
import {BsInfoCircle} from "react-icons/bs";
import {FaUserTie} from "react-icons/fa6";
import getAvatar from "../../../../../helpers/getAvatar";
import {TbEdit, TbTrash} from "react-icons/tb";
import copyToClipboard from "copy-to-clipboard";
import {IoCopyOutline} from "react-icons/io5";
import {useRouteSearchParam} from "../../../../../envs/_current/history";
import getAliasName from "../../../../../helpers/getAliasName";

// Chọn Hội nhóm/Đơn vị (group_id) trong tổ chức sở hữu khóa học - copy pattern
// GroupInOrganizationField (TimetableForm.js) nhưng nhận owner_id trực tiếp thay vì
// đọc qua form.values.organization (Course không có field đó).
const GroupSelector = ({name, value, owner_id, onChange = NullAsDefault}) => {
    const t = useTranslate()

    const {response, isLoading} = useAsyncWithCache({
        cacheId: [organization_getGroups_Api.path, owner_id].join('/'),
        apiInfo: organization_getGroups_Api,
        query: {id: owner_id},
        once: true,
    })

    const options = useMemo(() => {
        const items = getResponseItems(response) || []
        return items.map((e) => ({
            value: e.id, label: `${e?.title} (${e?.member_count ?? 0} ${t('members')})`
        }))
    }, [response, t])

    return (
        <SelectWidget
            name={name}
            allowClear={true}
            loading={isLoading}
            className="w-full"
            value={value || undefined}
            options={options}
            placeholder={t('---Choose One---')}
            onChange={(v) => onChange(createSelectValue(name, v))}
        />
    )
}

// Bộ lọc nâng cao cho danh sách Ban điều hành - lọc theo Chức vụ / Tổ-Đội-Nhóm-Ban. Lấy option
// trực tiếp từ giá trị THẬT đang tồn tại trong course_teachers của CHÍNH khóa này (không qua bảng
// lookups trung gian) - luôn đúng dữ liệu thật, kể cả dữ liệu cũ, và dùng dropdown nhẹ (SelectWidget,
// giống GroupSelector) thay vì mở modal Lookup - đúng kỳ vọng thao tác nhanh của 1 ô lọc.
const CourseTeacherFilter = ({
    course_id,
    value: filter = {
        position: undefined,
        sub_group_name: undefined,
    },
    onChange: setValues = NullAsDefault,
}) => {
    const t = useTranslate()
    const handleChange = (name, value) => setValues({...filter, [name]: value})

    // Lọc chéo: mỗi khi đổi 1 trong 2 lựa chọn, gọi lại API kèm giá trị bên còn lại đang chọn -
    // để ô kia chỉ hiện đúng những giá trị THỰC SỰ đi cùng lựa chọn hiện tại (vd đã chọn "Ban Ẩm
    // thực" thì Chức vụ chỉ còn các chức vụ tồn tại trong Ban Ẩm thực).
    const {response, isLoading} = useAsyncWithCache({
        cacheId: [
            course_getCourseTeacherFilterOptions_Api.path,
            course_id,
            filter.position ?? '',
            filter.sub_group_name ?? '',
        ].join('/'),
        apiInfo: course_getCourseTeacherFilterOptions_Api,
        query: {id: course_id},
        values: {position: filter.position, sub_group_name: filter.sub_group_name},
    })
    const {positions = [], sub_group_names = []} = getResponseItem(response) ?? {}

    const toOptions = (values) => values.map((v) => ({label: v, value: v}))

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/*<div className="flex flex-col gap-1">*/}
            {/*    <span className="text-xs text-color-400 uppercase tracking-wider">{t('group / ministry')}</span>*/}
            {/*    <GroupSelector*/}
            {/*        name="group_id"*/}
            {/*        value={filter.group_id}*/}
            {/*        owner_id={owner_id}*/}
            {/*        onChange={(e) => handleChange('group_id', e?.target?.value)}*/}
            {/*    />*/}
            {/*</div>*/}
            <div className="flex flex-col gap-1">
                <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                    {t('sub group title')}
                </span>
                <SelectWidget
                    className="w-full"
                    name="sub_group_name"
                    value={filter.sub_group_name}
                    allowClear={true}
                    loading={isLoading}
                    placeholder={t('click to select')}
                    onChange={(value) => handleChange('sub_group_name', value)}
                    options={toOptions(sub_group_names)}
                />
            </div>
            <div className="flex flex-col gap-1">
                <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                    {t('position')}
                </span>
                <SelectWidget
                    className="w-full"
                    name="position"
                    value={filter.position}
                    allowClear={true}
                    loading={isLoading}
                    placeholder={t('click to select')}
                    onChange={(value) => handleChange('position', value)}
                    options={toOptions(positions)}
                />
            </div>
        </div>
    )
}

// 3 field Tổ/Đội/Nhóm/Ban - Chức vụ - Nhiệm vụ trước đây đứng CHUNG kiểu dáng (cùng ô input trơn,
// cùng label ngắn) trong 1 khối liền, không phân biệt được field nào để làm gì (bạn báo). Sắp xếp
// lại theo mức tương quan, dùng khung Section (ModernForm.js) sẵn có của app: nhóm "Vai trò" (Tổ/Đội
// THUỘC nhóm nào + Chức vụ LÀ GÌ trong nhóm đó - 2 field cùng trả lời "vị trí trong Ban điều hành",
// đặt cạnh nhau) tách khỏi nhóm "Nhiệm vụ cụ thể" (mô tả công việc THỰC TẾ, khác hẳn chức danh) và
// "Ghi chú" (thông tin phụ, không thuộc 2 nhóm trên) - mỗi Section có subtitle giải thích nhóm này
// dùng để làm gì, mỗi Field có ví dụ thật trong placeholder thay vì chỉ "chọn hoặc tạo...". Dùng
// CHUNG cho cả Thêm/Sửa (trước đây lặp y hệt 2 lần) - sửa 1 chỗ, cả 2 modal cùng đổi.
const CourseTeacherRoleFields = ({
    course_id, sub_group_name, setSubGroupName, position, setPosition, task_name, setTaskName, notes, setNotes,
}) => {
    const t = useTranslate()
    return (
        <>
            <Section
                title={t('course committee role section title')}
                subtitle={t('course committee role section subtitle')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label={t('course committee sub group label')}>
                        <LookupField
                            createIfNotExists={true}
                            defaultValue={sub_group_name}
                            apiPath={`/lookup/course/${course_id}/field/sub_group_name/gets`}
                            name="sub_group_name"
                            placeholder={t('choose or create sub group title')}
                            handleChange={(e) => setSubGroupName(e.target.value)}
                        />
                    </Field>
                    <Field label={t('course committee position label')}>
                        <LookupField
                            createIfNotExists={true}
                            defaultValue={position}
                            apiPath={`/lookup/course/${course_id}/field/position/gets`}
                            name="position"
                            placeholder={t('course committee position placeholder')}
                            handleChange={(e) => setPosition(e.target.value)}
                        />
                    </Field>
                </div>
            </Section>
            <Section
                title={t('task name')}
                subtitle={t('course committee task section subtitle')}>
                <Field>
                    <Input.TextArea
                        name="task_name"
                        rows={3}
                        maxLength={500}
                        value={task_name || ''}
                        placeholder={t('enter task name')}
                        onChange={(e) => setTaskName(e.target.value)}
                    />
                </Field>
            </Section>
            <Section
                title={t('notes')}
                subtitle={t('course committee notes section subtitle')}>
                <Field>
                    <Input.TextArea
                        name="notes"
                        rows={3}
                        maxLength={500}
                        value={notes || ''}
                        placeholder={t('enter notes')}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </Field>
            </Section>
        </>
    )
}

const AddCourseTeacherModal = ({course, open, onCancel, onAssigned}) => {
    const t = useTranslate()
    const onError = notifyOnError(t)
    const course_id = getId(course)
    const owner_id = getId(course?.owner)
    const owner_type = getType(course?.owner)

    const [teacher, setTeacher] = useState(null)
    const [group_id, setGroupId] = useState(null)
    const [position, setPosition] = useState(null)
    const [sub_group_name, setSubGroupName] = useState(null)
    const [task_name, setTaskName] = useState(null)
    const [notes, setNotes] = useState(null)

    const reset = () => {
        setTeacher(null)
        setGroupId(null)
        setPosition(null)
        setSubGroupName(null)
        setTaskName(null)
        setNotes(null)
    }

    const {handleAsyncAction: onAssign, isLoading} = useAsyncAction({
        apiInfo: course_assignTeacherToCourse_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.CREATE, t)
            reset()
            onAssigned()
        },
    })

    const teacher_id = getId(teacher)

    return (
        <Modal
            className="custom-modal"
            open={open}
            onCancel={() => {
                reset()
                onCancel()
            }}
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('add teacher / person in charge')}
                </div>
            }
            okText={t('add')}
            okButtonProps={{
                disabled: !teacher_id, loading: isLoading,
                className: 'rounded-lg',
            }}
            onOk={() =>
                onAssign(
                    {
                        teacher_name: teacher?.fullname,
                        group_id,
                        position,
                        sub_group_name,
                        task_name,
                        notes,
                    },
                    {course_id, teacher_id}
                )
            }
            cancelText={t('close')}
            cancelButtonProps={{
                type: 'default',
                className: 'no-shadow no-border rounded-lg bg-transparent font-medium text-color-300',
            }}
            destroyOnHidden={true}>
            <div className="flex flex-col gap-4">
                <div className="flex gap-3 px-3 py-2 bg-blue-50 rounded-lg">
                    <BsInfoCircle size={18} className="text-blue-400"/>
                    <div className="flex flex-col flex-1 gap-1">
                        <span className="text-color-200 italic tracking-wider">
                            {t('course teacher description')}
                        </span>
                    </div>
                </div>

                <Field label={t('teacher')} required>
                    <TeacherSelector
                        name="teacher_id"
                        value={teacher}
                        owner_id={owner_id}
                        owner_type={owner_type}
                        not_course_id={course_id}
                        onChange={(e) => setTeacher(e?.target?.value)}
                    />
                </Field>
                <CourseTeacherRoleFields
                    course_id={course_id}
                    sub_group_name={sub_group_name} setSubGroupName={setSubGroupName}
                    position={position} setPosition={setPosition}
                    task_name={task_name} setTaskName={setTaskName}
                    notes={notes} setNotes={setNotes}
                />
                {/*<div className="flex flex-col gap-1">*/}
                {/*    <Field label={t('choose group / ministry')}>*/}
                {/*        <GroupSelector*/}
                {/*            name="group_id"*/}
                {/*            value={group_id}*/}
                {/*            owner_id={owner_id}*/}
                {/*            onChange={(e) => setGroupId(e?.target?.value)}*/}
                {/*        />*/}
                {/*    </Field>*/}
                {/*</div>*/}
                {/* Không chọn được Hội nhóm/Đơn vị có sẵn (group_id trống) thì vẫn có thể tự đặt
                    tên phân nhóm nhanh (Tổ/Đội/Nhóm/Ban) - tương tự sub_class_name của Học viên,
                    không cần tạo 1 Group thật trong hệ thống. */}
            </div>
        </Modal>
    )
}

// Sửa chức vụ/Hội nhóm/Tổ-đội của 1 giáo viên đã có trong Ban điều hành - KHÔNG cho đổi giáo viên
// (đúng theo editTeacherFromCourse ở BE: chỉ nhận teacher_name/group_id/position/sub_group_name,
// không có teacher_id) - muốn đổi người thì xóa rồi thêm lại.
const EditCourseTeacherModal = ({item, course_id, owner_id, owner_type, open, onCancel, onUpdated}) => {
    const t = useTranslate()
    const onError = notifyOnError(t)

    const [group_id, setGroupId] = useState(item?.group?.id ?? null)
    const [position, setPosition] = useState(item?.position ?? null)
    const [sub_group_name, setSubGroupName] = useState(item?.sub_group_name ?? null)
    const [task_name, setTaskName] = useState(item?.task_name ?? null)
    const [notes, setNotes] = useState(item?.notes ?? null)

    const {handleAsyncAction: onUpdate, isLoading} = useAsyncAction({
        apiInfo: course_editTeacherFromCourse_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onUpdated()
        },
    })

    return (
        <Modal
            className="custom-modal"
            open={open}
            onCancel={onCancel}
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('edit teacher / person in charge')}
                </div>
            }
            okText={t('save')}
            okButtonProps={{loading: isLoading}}
            onOk={() =>
                onUpdate(
                    {group_id, position, sub_group_name, task_name, notes},
                    {id: getId(item)}
                )
            }
            cancelText={t('close')}
            destroyOnHidden={true}>
            <div className="flex flex-col gap-4">
                <div className="px-3 py-2 flex items-center gap-2 border border-color-50 background shadow-sm rounded-xl">
                    <Avatar
                        size={44}
                        src={getAvatar(item?.teacher) || getAvatar(item?.teacher?.user)}
                        icon={<FaUserTie size={20} className="text-color-500"/>}
                        className="flex flex-center background-200"
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                        <span className="font-semibold text-sm text-color-000">
                            {item?.teacher?.prefix_title} {getTitle(item?.teacher) || item?.teacher_name}
                        </span>
                        {renderIf(
                            item?.teacher?.teacher_idcode,
                            <span className="font-mono font-semibold text-blue-500 hover:text-blue-700 cursor-pointer">
                                #{item?.teacher?.teacher_idcode}
                            </span>
                        )}
                    </div>
                </div>
                <CourseTeacherRoleFields
                    course_id={course_id}
                    sub_group_name={sub_group_name} setSubGroupName={setSubGroupName}
                    position={position} setPosition={setPosition}
                    task_name={task_name} setTaskName={setTaskName}
                    notes={notes} setNotes={setNotes}
                />

                {/*<div className="flex flex-col gap-1">*/}
                {/*    <span className="text-xs text-color-400 uppercase tracking-wider">{t('group / ministry')}</span>*/}
                {/*    <GroupSelector*/}
                {/*        name="group_id"*/}
                {/*        value={group_id}*/}
                {/*        owner_id={owner_id}*/}
                {/*        onChange={(e) => setGroupId(e?.target?.value)}*/}
                {/*    />*/}
                {/*</div>*/}
            </div>
        </Modal>
    )
}

// Layout làm lại theo mockup bạn gửi - đổi so với bản cũ:
// - Chức vụ (position) dời từ dòng metadata (cạnh mã GV) lên NGAY CẠNH tên, làm badge vai trò nổi
//   bật đầu tiên đập vào mắt (cùng field/style pill cũ - border-secondary, chỉ đổi CHỖ ĐỨNG).
// - Mã giáo viên xuống dòng riêng ngay dưới tên, làm nhạt màu (color-300 thay blue-500) - phụ,
//   không cần nổi ngang hàng tên/badge nữa.
// - "Bộ phận" (sub_group_name) đổi value từ text trơn sang chip màu xanh (dễ quét mắt hơn, khớp
//   mockup) - label ngắn RIÊNG cho card này ("course committee sub group short label" = "Bộ phận"),
//   KHÔNG đổi key "sub group title" gốc ("Tổ / Đội / Nhóm / Ban") vì key đó còn dùng ở filter + form
//   thêm/sửa (đổi chung sẽ lệch nghĩa 2 chỗ đó).
// - Sửa/Xoá đổi từ 2 icon nhỏ CHỈ HIỆN KHI HOVER (contextIcon, dễ bị bỏ sót trên mobile - không có
//   hover) sang 2 nút bo viền LUÔN HIỆN RÕ, xếp cạnh nhau bên phải card - bỏ hẳn custom-item-render/
//   list-item/contextIcon (không còn cần cơ chế hover-reveal nữa).
const renderItem = ({onRemove = NullAsDefault, onEdit = NullAsDefault} = {}) => (item, index) => {
    const {teacher, teacher_name, group, position, sub_group_name, task_name, notes} = item ?? {}
    return (
        <Translate key={index}>
            {(t) => (
                <div className="p-4 flex items-start gap-3 background border border-color-50 rounded-xl shadow-sm hover:shadow-md transition-all">
                    <Avatar
                        size={44}
                        src={getAvatar(teacher)}
                        icon={<FaUserTie size={20} className="text-color-500"/>}
                        className="mt-0.5 flex flex-center background-200 flex-shrink-0"
                    />
                    <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                            <span className="font-bold text-base text-color-000 truncate">
                                {teacher.patron_saint && (
                                    <span className="mr-1 font-medium text-sm text-gray-600">
                                        {getAliasName(teacher, 'patron_saint')}
                                    </span>
                                )}
                                {teacher?.prefix_title} {getTitle(teacher) || teacher_name}
                            </span>
                            {position && (
                                <span className="px-2 py-0.5 border border-secondary-400 background rounded-full font-bold text-2xs text-secondary-500 whitespace-no-wrap">
                                    {position}
                                </span>
                            )}
                        </div>
                        {renderIf(
                            teacher?.teacher_idcode,
                            <span
                                onClick={() => {
                                    copyToClipboard(teacher?.teacher_idcode)
                                    message.info(t('copied'))
                                }}
                                className="w-fit font-mono text-xs text-color-300 hover:text-blue-500 cursor-pointer">
                                #{teacher?.teacher_idcode}
                                <IoCopyOutline size={10} className="ml-1"/>
                            </span>
                        )}
                        <div className="mt-1 flex flex-col gap-1">
                            {renderIf(
                                group?.title || sub_group_name,
                                <ItemInformation
                                    showIcon={true}
                                    label="course committee sub group short label"
                                    value={renderIf(
                                        group?.title || sub_group_name,
                                        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-full text-2xs font-semibold whitespace-no-wrap">
                                            {group?.title || sub_group_name}
                                        </span>
                                    )}
                                    className="text-xs tracking-wider"
                                />
                            )}
                            {renderIf(
                                task_name,
                                <ItemInformation showIcon={true} label="task name" value={task_name} className="text-xs tracking-wider"/>
                            )}
                        </div>
                        {renderIf(
                            notes,
                            <span className="mt-1 px-2 py-1 italic bg-slate-50 rounded-lg">
                                <span className="font-semibold text-slate-500 flex-shrink-0">
                                    {`* ${t('note')} : `}
                                </span>
                                <span className="text-slate-400">
                                    {notes}
                                </span>
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => onEdit(item)}
                            className="px-3 py-1.5 flex items-center gap-1.5 border border-color-200 hover:border-blue-400 hover:bg-blue-50 rounded-lg text-xs font-semibold text-color-300 hover:text-blue-600 transition-colors">
                            <TbEdit size={14}/>
                            {t('edit')}
                        </button>
                        <button
                            type="button"
                            onClick={() => onRemove(item)}
                            style={{width: 32, height: 32}}
                            className="flex flex-center border border-color-200 hover:border-primary-400 hover:bg-primary-50 rounded-lg text-color-300 hover:text-primary-500 transition-colors">
                            <TbTrash size={14}/>
                        </button>
                    </div>
                </div>
            )}
        </Translate>
    )
}

// Component "thuần props" - nhận course qua `item`, tái dùng được ở bất kỳ cây nào (AppLocals lẫn
// Workspace/Course/containers/CourseDetail.js) mà không phụ thuộc CourseContext - cùng convention
// với CourseStudentsSearchBinding/CourseSubjectList (nhận `item` trực tiếp, không qua Context).
export const CourseTeachers = ({
                                   item: course,
                                   filter_actions = {
                                       sortBy: 'sort-by',
                                       keyword: 'keyword',
                                       position: 'position',
                                       sub_group_name: 'sub-group-name',
                                   }
}) => {
    const t = useTranslate()
    const course_id = getId(course)
    const owner_id = getId(course?.owner)
    const owner_type = getType(course?.owner)
    const {edit} = course ?? {}

    const [showAddModal, setShowAddModal] = useState(false)
    const [editingItem, setEditingItem] = useState(null)
    const [refreshToken, setRefreshToken] = useState(Date.now())


    const keyword = useRouteSearchParam(filter_actions.keyword)
    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const position = useRouteSearchParam(filter_actions.position)
    const sub_group_name = useRouteSearchParam(filter_actions.sub_group_name)

    const onError = notifyOnError(t)

    const {handleAsyncAction: onRevoke} = useAsyncAction({
        apiInfo: course_revokeTeacherFromCourse_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.DELETE, t)
            setRefreshToken(Date.now())
        },
    })

    const onRemove = (item) => {
        showDeleteConfirmDialog({
            translate: t,
            title: 'revoke teacher',
            message: 'are you sure you want to revoke this teacher',
            onOk: () => onRevoke({}, {id: getId(item)}),
        })
    }

    const [pager_widget] = usePaginationHelper({
        filter_params: {course_id},
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onSortChange({sortBy})
            onFilterChange({
                keyword, position, sub_group_name,
            })
        },
        pagerInfo: {
            RenderEntity: React.Fragment,
            renderEmpty: renderEmptyHolder,
            PaginationMeta: NullAsDefault,
            apiInfo: course_getCourseTeachers_Api,
            query: bindQueryParam({id: course_id}),
            renderItem: renderItem({onRemove, onEdit: setEditingItem}),
            renderWrap: (child, __, count) =>
                // Trước đây divide-y (các dòng dính liền, chỉ có gạch ngang phân cách) - đổi sang
                // gap-3 để mỗi thành viên là 1 card RIÊNG, tách bạch rõ ràng, khớp mockup bạn gửi
                // (card viền/bo góc/khoảng cách rõ, không phải list dính liền).
                count ? <div className="flex flex-col gap-3">{child}</div> : <EmptyHolder/>,
        },
        query_params: filter_actions,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        AdvancedFilter: CourseTeacherFilter,
        isToggleDefault: false,
        withKey: createPagerKey([course_id, refreshToken]),
        dependencies: [owner_id, owner_type, course_id, refreshToken, position, sub_group_name],
    })

    return (
        <div className="flex flex-col gap-4">
            {renderIf(
                edit,
                <div className="flex justify-end">
                    <Button
                        type="primary"
                        className="rounded-lg no-border"
                        onClick={() => setShowAddModal(true)}>
                        {t('add teacher / person in charge')}
                    </Button>
                </div>
            )}
            {pager_widget}
            <AddCourseTeacherModal
                course={course}
                open={showAddModal}
                onCancel={() => setShowAddModal(false)}
                onAssigned={() => {
                    setShowAddModal(false)
                    setRefreshToken(Date.now())
                }}
            />
            {editingItem && (
                <EditCourseTeacherModal
                    item={editingItem}
                    course_id={course_id}
                    owner_id={owner_id}
                    owner_type={owner_type}
                    open={!!editingItem}
                    onCancel={() => setEditingItem(null)}
                    onUpdated={() => {
                        setEditingItem(null)
                        setRefreshToken(Date.now())
                    }}
                />
            )}
        </div>
    )
}

// Wrapper đọc CourseContext - dùng cho route tab trong AppLocals/Course/CourseSet/courseRouteEntities.js.
const CourseTeacherTab = () => {
    const {item} = useContext(CourseContext)
    return <CourseTeachers item={item}/>
}

export default CourseTeacherTab
